<template>
  <div class="overflow-y-hidden w-full h-[100lvh] bg-cw-green-dark flex flex-col fixed left-0 top-0 z-50">
    <div class="px-16 py-24 heightMd:pt-8 heightSm:pt-8">
      <div class="flex flex-col justify-center items-center h-full">
        <h1 class="text-2xl heightMd:text-lg heightSm:text-[16px] font-bold text-white tracking-normal">Make a word</h1>
        <div class="mt-5 heightMd:mt-2 text-lg heightMd:text-xs flex items-center heightMd:gap-5 gap-7 mb-12 heightMd:mb-5 uppercase font-bold">
          <p class="heightMd:text-xs" :class="checkWord(currentWord) === 'plan' ? 'text-cw-orange-alt' : 'text-cw-green-muted'">Plan</p>
          <p class="heightMd:text-xs" :class="checkWord(currentWord) === 'save' ? 'text-cw-orange-alt' : 'text-cw-green-muted'">Save</p>
          <p class="heightMd:text-xs" :class="checkWord(currentWord) === 'invest' ? 'text-cw-orange-alt' : 'text-cw-green-muted'">Invest</p>
        </div>
        <LetterInput @deleteLetter="deleteLetter" :letters="currentWord" :size="maxSize" mode="input" />
        <p class="mt-5 text-lg text-cw-red">{{ errorMessage }}</p>
        <div class="mt-14 heightMd:mt-6 *:!w-full">
          <BaseButton
            class="heightMd:px-9 px-16 flex items-center justify-center !rounded-xl text-lg font-bold heightMd:text-sm"
            backgroundColor="var(--color-cw-green)"
            borderColor="var(--color-cw-green-border-shadow)"
            @click="makeWord"
            :disabled="!isCorrectWord || selectedCards.length < 4"
          >
            Submit
          </BaseButton>
        </div>

        <PlayersHand cursor="pointer" :cards="userPlayersHandArranged!" class="absolute bottom-16 heightMd:bottom-6" :disabledCards="selectedCards" @cardClick="handleCardClick" />
      </div>
    </div>
    <a href="" @click.prevent="goToGameAreaScreen" class="absolute heightMd:top-6 heightMd:left-14 top-12 left-20">
      <CloseIcon class="heightMd:w-6 heightMd:h-6 w-8 h-8 text-white" />
    </a>

    <GameTimer
      @timeUp="handleTimeout"
      :key="currentTurnPlayerProfile.actionTimeout"
      :timeRemaining="getActionTimeout()"
      v-if="currentTurnPlayerProfile && currentTurnPlayerProfile.actionTimeout > 0"
      class="absolute bottom-[10vh] left-4"
    />

    <div v-if="totalScore" class="absolute heightMd:top-6 heightMd:right-14 top-12 right-20 bg-cw-green-alt text-2xl text-white font-semibold rounded-[16px] px-6 py-4">{{ totalScore }} points</div>
  </div>
</template>

<script setup lang="ts">
import LetterInput from '@/components/game-play/LetterInput.vue'
import { computed, ref, inject } from 'vue'
import BaseButton from '@/components/base/BaseButton.vue'
import PlayersHand from '@/components/game-play/PlayersHand.vue'
import CloseIcon from '@/assets/svgs/icons/close.svg'
import type { CardCategoryT, CardT } from '@/types/Game'
import { useGameStore } from '@/stores/game'
import { storeToRefs } from 'pinia'
import { useGameArea } from '@/composables/useGameArea'
import { useHelpers } from '@/composables/useHelpers'
import { GameEvent } from '@/types/Events'
import useSocket from '@/composables/useSocket'
import { useAppStore } from '@/stores/app'
import GameTimer from '@/components/interface/GameTimer.vue'

const showLoader = inject('showLoader') as InjectedFunctionT
const hideLoader = inject('hideLoader') as InjectedFunctionT

const { deferGameState } = useAppStore()
const { userPlayersHandArranged, currentTurnPlayerProfile } = storeToRefs(useGameStore())
const { getActionTimeout } = useGameStore()
const { goToGameAreaScreen, setActiveGameAreaScreen, formedWordCards } = useGameArea()
const { handleCustomError } = useHelpers()
const socket = useSocket()

const selectedCards = ref<CardT[]>([])
const inputLetters = computed(() => selectedCards.value.map(card => card.letter))
const errorMessage = ref<string | null>(null)

const maxSize: number = 6

const totalScore = computed(() => {
  return selectedCards.value.reduce((acc, card) => acc + card.value, 0)
})

const currentWord = computed((): string => {
  return inputLetters.value.join('')
})

const isCorrectWord = computed((): boolean => {
  const targetWords = ['plan', 'save', 'invest']
  const currentWordValue = currentWord.value.toLowerCase()

  return targetWords.some(targetWord => {
    if (currentWordValue.length !== targetWord.length) return false

    return currentWordValue.split('').every((letter, index) => {
      return letter === targetWord[index] || letter === '*'
    })
  })
})

const checkWord = (word: string) => {
  const targetWords = ['plan', 'save', 'invest']

  const matchedWord = targetWords.find(targetWord => {
    if (word.length !== targetWord.length) return false

    return word
      .toLowerCase()
      .split('')
      .every((letter, index) => {
        return letter === targetWord[index] || letter === '*'
      })
  })

  return matchedWord || null
}

const handleCardClick = (card: CardT): void => {
  const cardIndex = selectedCards.value.findIndex(selectedCard => selectedCard.id === card.id)
  if (cardIndex !== -1) {
    selectedCards.value = selectedCards.value.filter((_, index) => index !== cardIndex)
  } else {
    if (selectedCards.value.length < maxSize) {
      selectedCards.value = [...selectedCards.value, card]
    }
  }
}

const deleteLetter = (): void => {
  if (selectedCards.value.length > 0) {
    selectedCards.value.pop()
  }
}

const verifyCardTypes = (cards: CardT[], type: CardCategoryT) => {
  // console.log(
  //   cards.map(card => card.category),
  //   type
  // )

  return cards.every(card => card.category === type)
}

const makeWord = async () => {
  try {
    if (!verifyCardTypes(selectedCards.value, checkWord(currentWord.value) as CardCategoryT)) {
      errorMessage.value = 'You can only use cards of the same type to form a word.'
      return
    }
    errorMessage.value = ''
    showLoader()
    deferGameState()
    await socket.sendEventAsync<CardT[]>(GameEvent.MakeWord, {
      cardIds: selectedCards.value.map(card => card.id)
    })
    formedWordCards.value = selectedCards.value
    setActiveGameAreaScreen('WORD_CONGRATULATORY_SCREEN')
  } catch (error: any) {
    errorMessage.value = error
    handleCustomError(error, false)
  } finally {
    hideLoader()
  }
}

const handleTimeout = () => {
  // Clear any error messages and return to game screen gracefully
  errorMessage.value = ''
  hideLoader() // Ensure any loading states are cleared
  goToGameAreaScreen()
}
</script>

<style scoped></style>
